import React, { forwardRef } from 'react';
import { fmtKwh, fmtNum, fmtPct } from '../../../../src/lib/funnelUtils';
import { AnalyticsExportInput } from '../../../../src/lib/analyticsComercialExport';

/** Mesmo payload que alimenta o Excel e o PDF (buildExportInput) — a imagem passou
 *  a ser uma terceira saída do MESMO dado, e não um resumo próprio que divergia. */
export interface ResumoVisualData extends AnalyticsExportInput {
    insights: string[];
}

interface ResumoVisualCardProps {
    data: ResumoVisualData;
}

const VERDE = '#5F6C37';
const VERDE_ESCURO = '#212913';
const AMBAR = '#F6A937';
const AZUL = '#2563EB';

/** Cabeçalho de seção — barra vertical + título, repetido em todos os blocos. */
const Secao: React.FC<{ titulo: string; nota?: string; children: React.ReactNode }> = ({ titulo, nota, children }) => (
    <div className="mb-10">
        <div className="flex items-baseline gap-4 mb-4">
            <span style={{ width: 8, height: 30, background: VERDE, borderRadius: 4, display: 'inline-block' }} />
            <p className="text-3xl font-extrabold text-gray-800">{titulo}</p>
            {nota && <p className="text-xl text-gray-400">{nota}</p>}
        </div>
        {children}
    </div>
);

/** Linha de tabela com faixa proporcional ao valor, para o número virar leitura visual. */
const LinhaBarra: React.FC<{
    rotulo: string;
    destaque: string;
    apoio?: string;
    pct: number;
    cor?: string;
}> = ({ rotulo, destaque, apoio, pct, cor = VERDE }) => (
    <div className="bg-white rounded-2xl border border-gray-100 px-6 py-4 mb-2">
        <div className="flex items-center justify-between mb-2">
            <span className="text-2xl font-bold text-gray-800">{rotulo}</span>
            <span className="text-2xl font-bold" style={{ color: cor }}>
                {destaque}
                {apoio && <span className="text-xl font-semibold text-gray-400 ml-3">{apoio}</span>}
            </span>
        </div>
        <div style={{ height: 10, background: '#EEF0EA', borderRadius: 6 }}>
            <div style={{ height: 10, width: `${Math.max(2, Math.min(100, pct))}%`, background: cor, borderRadius: 6 }} />
        </div>
    </div>
);

/**
 * Card de 1080px capturado por html2canvas para virar imagem WhatsApp-friendly.
 * Renderizado fora da viewport e só enquanto a exportação acontece.
 */
const ResumoVisualCard = forwardRef<HTMLDivElement, ResumoVisualCardProps>(({ data }, ref) => {
    const maxFunil = Math.max(1, ...data.funil.map(f => f.leads));
    const maxCanal = Math.max(1, ...data.canal.map(c => c.kwhAprovado));
    const maxRanking = Math.max(1, ...data.ranking.map(r => r.kwhAprovado));
    const totalPerdas = data.motivosPerda.reduce((s, [, n]) => s + n, 0);
    const maxSegmento = Math.max(1, ...data.segmento.map(s => s.total));

    const ranking = data.ranking.slice(0, 8);
    const motivos = data.motivosPerda.slice(0, 6);
    const segmentos = data.segmento.slice(0, 6);

    const geradoEm = new Date().toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

    return (
        <div
            ref={ref}
            style={{ width: 1080, position: 'fixed', left: -99999, top: 0, fontFamily: 'Inter, system-ui, sans-serif' }}
            className="bg-white"
        >
            {/* ─── Cabeçalho ─── */}
            <div className="p-14" style={{ background: `linear-gradient(135deg, ${VERDE_ESCURO} 0%, ${VERDE} 60%, #8a9462 100%)` }}>
                <p className="text-white/70 text-2xl font-bold tracking-widest uppercase">Vigor Energy</p>
                <h1 className="text-white text-5xl font-extrabold mt-2">Dashboard Comercial</h1>
                <p className="text-white/90 text-3xl font-semibold mt-2">{data.periodoLabel}</p>
                {data.filtrosLabel && (
                    <p className="text-white/70 text-xl mt-3">Filtros: {data.filtrosLabel}</p>
                )}
                <p className="text-white/50 text-lg mt-4">Gerado em {geradoEm}</p>
            </div>

            <div className="px-14 pt-12 pb-14" style={{ background: '#F9FAFB' }}>
                {/* ─── Indicadores ─── */}
                <Secao titulo="Indicadores do período">
                    <div className="grid grid-cols-4 gap-4">
                        {data.kpis.map(([rotulo, valor]) => (
                            <div key={rotulo} className="bg-white rounded-2xl p-6 border border-gray-100">
                                <p className="text-gray-400 text-base font-bold uppercase leading-tight">{rotulo}</p>
                                <p className="text-4xl font-extrabold text-gray-900 mt-2">{valor}</p>
                            </div>
                        ))}
                    </div>
                </Secao>

                {/* ─── Meta x Realizado (só gestão, e só quando o período é um mês fechado) ─── */}
                {data.meta && (
                    <Secao titulo="Meta × Realizado" nota={data.meta.mesLabel}>
                        <div className="bg-white rounded-2xl border border-gray-100 px-8 py-7">
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <p className="text-gray-400 text-lg font-bold uppercase">Realizado</p>
                                    <p className="text-5xl font-extrabold" style={{ color: VERDE }}>{fmtKwh(data.meta.realizado)}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-gray-400 text-lg font-bold uppercase">Meta</p>
                                    <p className="text-4xl font-bold text-gray-700">{fmtKwh(data.meta.meta)}</p>
                                </div>
                            </div>
                            <div style={{ height: 20, background: '#EEF0EA', borderRadius: 10 }}>
                                <div style={{
                                    height: 20,
                                    width: `${Math.max(2, Math.min(100, data.meta.pct))}%`,
                                    background: data.meta.pct >= 100 ? VERDE : AMBAR,
                                    borderRadius: 10,
                                }} />
                            </div>
                            <div className="flex justify-between mt-3">
                                <p className="text-2xl font-bold" style={{ color: data.meta.pct >= 100 ? VERDE : AMBAR }}>
                                    {fmtPct(data.meta.pct)} da meta
                                </p>
                                <p className="text-2xl font-semibold text-gray-500">
                                    {data.meta.gap > 0 ? `Faltam ${fmtKwh(data.meta.gap)}` : 'Meta batida'}
                                </p>
                            </div>
                        </div>
                    </Secao>
                )}

                {/* ─── Funil ─── */}
                {data.funil.length > 0 && (
                    <Secao titulo="Funil por etapa" nota="leads · kWh">
                        {data.funil.map(f => (
                            <LinhaBarra
                                key={f.etapa}
                                rotulo={f.etapa}
                                destaque={fmtNum(f.leads)}
                                apoio={fmtKwh(f.kwh)}
                                pct={(f.leads / maxFunil) * 100}
                            />
                        ))}
                    </Secao>
                )}

                {/* ─── Status e motivos de perda, lado a lado ─── */}
                <div className="grid grid-cols-2 gap-8">
                    {data.statusPanel.length > 0 && (
                        <Secao titulo="Painel de status">
                            {data.statusPanel.map(s => (
                                <LinhaBarra
                                    key={s.status}
                                    rotulo={s.status}
                                    destaque={fmtNum(s.qtd)}
                                    apoio={fmtPct(s.pct)}
                                    pct={s.pct}
                                    cor={AZUL}
                                />
                            ))}
                        </Secao>
                    )}

                    {motivos.length > 0 && (
                        <Secao titulo="Motivos de perda">
                            {motivos.map(([motivo, qtd]) => (
                                <LinhaBarra
                                    key={motivo}
                                    rotulo={motivo}
                                    destaque={fmtNum(qtd)}
                                    apoio={totalPerdas > 0 ? fmtPct((qtd / totalPerdas) * 100) : undefined}
                                    pct={totalPerdas > 0 ? (qtd / totalPerdas) * 100 : 0}
                                    cor="#DC2626"
                                />
                            ))}
                        </Secao>
                    )}
                </div>

                {/* ─── Canais ─── */}
                {data.canal.length > 0 && (
                    <Secao titulo="Desempenho por canal" nota="kWh aprovado · conversão">
                        {data.canal.map(c => (
                            <LinhaBarra
                                key={c.canal}
                                rotulo={`${c.canal} — ${fmtNum(c.leads)} leads`}
                                destaque={fmtKwh(c.kwhAprovado)}
                                apoio={fmtPct(c.conversao)}
                                pct={(c.kwhAprovado / maxCanal) * 100}
                            />
                        ))}
                    </Secao>
                )}

                {/* ─── Ranking ─── */}
                {ranking.length > 0 && (
                    <Secao titulo="Ranking de consultores" nota={`top ${ranking.length} por kWh aprovado`}>
                        {ranking.map((r, i) => (
                            <div key={r.nome} className="bg-white rounded-2xl border border-gray-100 px-6 py-4 mb-2">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-2xl font-bold text-gray-800">
                                        <span style={{ color: VERDE }}>{i + 1}º</span> {r.nome}
                                    </span>
                                    <span className="text-2xl font-bold" style={{ color: VERDE }}>{fmtKwh(r.kwhAprovado)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xl text-gray-400">
                                        {fmtNum(r.leads)} leads · {fmtNum(r.aprovados)} aprovados · {fmtNum(r.assinados)} assinados
                                    </span>
                                    <span className="text-xl font-semibold text-gray-500">{fmtPct(r.conversao)} conv.</span>
                                </div>
                                <div style={{ height: 10, background: '#EEF0EA', borderRadius: 6, marginTop: 10 }}>
                                    <div style={{ height: 10, width: `${Math.max(2, (r.kwhAprovado / maxRanking) * 100)}%`, background: VERDE, borderRadius: 6 }} />
                                </div>
                            </div>
                        ))}
                    </Secao>
                )}

                {/* ─── Segmentos ─── */}
                {segmentos.length > 0 && (
                    <Secao titulo="Segmentos" nota="leads · conversão">
                        {segmentos.map(s => (
                            <LinhaBarra
                                key={s.segmento}
                                rotulo={s.segmento}
                                destaque={fmtNum(s.total)}
                                apoio={`${fmtPct(s.conversao)} · ${fmtKwh(s.kwh)}`}
                                pct={(s.total / maxSegmento) * 100}
                                cor="#7C3AED"
                            />
                        ))}
                    </Secao>
                )}

                {/* ─── Leituras automáticas ─── */}
                {data.insights.length > 0 && (
                    <Secao titulo="Leituras do período">
                        <div className="bg-white rounded-2xl border border-gray-100 px-8 py-6">
                            {data.insights.map((texto, i) => (
                                <p key={i} className="text-2xl text-gray-700 leading-relaxed" style={{ marginTop: i === 0 ? 0 : 14 }}>
                                    <span style={{ color: VERDE, fontWeight: 800 }}>•</span> {texto}
                                </p>
                            ))}
                        </div>
                    </Secao>
                )}
            </div>

            <div className="px-14 py-8" style={{ background: VERDE_ESCURO }}>
                <p className="text-white/60 text-xl">
                    VigorHub · Dashboard Comercial · {data.periodoLabel}
                    {data.filtrosLabel ? ` · ${data.filtrosLabel}` : ' · sem filtros aplicados'}
                </p>
            </div>
        </div>
    );
});
ResumoVisualCard.displayName = 'ResumoVisualCard';

export default ResumoVisualCard;
